import React, { useState, useEffect, useRef, useCallback } from 'react';
import swal from '@sweetalert/with-react';

import { filterArrayOfObjects, useDebouncedValue, deleteData, capitalizeFirstLetters, loadSpecificData } from '../../../js/Helpers';
import { saveDataToDatabase } from '../../../js/SaveFunctions';

import SortToggle from '../../SortToggle';
import UploadModal from '../../UploadModal';
import EditCourseData from '../modals/edit_course';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faEdit, faPlus, faUpload } from '@fortawesome/free-solid-svg-icons';
import { faTrashAlt } from '@fortawesome/free-regular-svg-icons';

const COURSE_TYPE_OPTIONS = [
	{value: 'general', display: 'General'},
	{value: 'special', display: 'Special'},
	{value: 'pullout', display: 'Pullout'},
	{value: 'lunch', display: 'Lunch'},
];

const getCourseTypeValue = (course_info = {}) => {
	if(`${course_info.is_special || '0'}` === '1') return 'special';
	if(`${course_info.is_pullout || '0'}` === '1') return 'pullout';
	if(`${course_info.is_lunch || '0'}` === '1') return 'lunch';
	return 'general';
};

const getCourseTypeDisplay = (course_info = {}) => {
	const course_type_value = getCourseTypeValue(course_info);
	const course_type_option = COURSE_TYPE_OPTIONS.find((option) => option.value === course_type_value);
	return course_type_value === 'general' ? '' : (course_type_option ? course_type_option.display : '');
};

const buildCourseDraft = (course_info = {}) => ({
	course_id: course_info.course_id,
	course_code: course_info.course_code || '',
	name: course_info.name || '',
	course_type: getCourseTypeValue(course_info),
});

const mergeSavedCourseIntoList = (course_list = [], old_course_id, saved_course) => {
	const old_course_id_string = `${old_course_id}`;
	const new_course_id_string = `${saved_course.course_id}`;
	const original_course_index = course_list.findIndex((course_info) => `${course_info.course_id}` === old_course_id_string);

	if(old_course_id_string === new_course_id_string)
	{
		return course_list.map((course_info) => (
			`${course_info.course_id}` === old_course_id_string ? saved_course : course_info
		));
	}

	const next_course_list = course_list.filter((course_info) => (
		`${course_info.course_id}` !== old_course_id_string && `${course_info.course_id}` !== new_course_id_string
	));

	if(original_course_index === -1) return [...next_course_list, saved_course];

	const safe_insert_index = Math.min(original_course_index, next_course_list.length);
	return [
		...next_course_list.slice(0, safe_insert_index),
		saved_course,
		...next_course_list.slice(safe_insert_index),
	];
};

const coursesMatch = (course_a = {}, course_b = {}) => {
	const normalized_a = buildCourseDraft(course_a);
	const normalized_b = buildCourseDraft(course_b);

	return normalized_a.course_code.trim() === normalized_b.course_code.trim()
		&& normalized_a.name.trim() === normalized_b.name.trim()
		&& normalized_a.course_type === normalized_b.course_type;
};

export default function DatabaseDataCourses({ schoolInfo, databaseData, setDatabaseData, setStudents, scheduleInfo, periods, teachers, students, classrooms, dataIsLoading, blocks, pulloutGroups, setPulloutGroups, handlePullNewData}) {
	const [isLoading, setIsLoading] = useState(true);
	const [handleChangeComplete, setHandleChangeComplete] = useState(false);
	
	const [filteredResults, setFilteredResults] = useState([]);
	const [displayResults, setDisplayResults] = useState([]);
	const [isSearching, setIsSearching] = useState(false);
	const [searchValue, setSearchValue] = useState('');
	const debouncedSearchValue = useDebouncedValue(searchValue, 120);
	
	const [showEditDataModal, setShowEditDataModal] = useState(false);
	const [showUploadModal, setShowUploadModal] = useState(false);
	const [currentDataID, setCurrentDataID] = useState(null);
	
	const [dontShowDeletePopup, setDontShowDeletePopup] = useState(false);
	const [editingCell, setEditingCell] = useState(null);
	const [cellDraft, setCellDraft] = useState(null);
	const [isSavingCell, setIsSavingCell] = useState(false);

	const filteredResultsRef = useRef(filteredResults);
	const activeEditorRef = useRef(null);
	const courseCodeInputRef = useRef(null);
	const courseNameInputRef = useRef(null);
	filteredResultsRef.current = filteredResults;
	
	const handleIsSearching = (e) => {
		if(e.keyCode !== 8)
		{
			setIsLoading(false);
			setIsSearching(true);
		}
		else
		{
			setIsLoading(true);
			setIsSearching(false);
		}
	};
	
	const handleChange = useCallback(() => {
		if(filteredResultsRef.current.length === 0 && dataIsLoading) return;
		
		const search_bar = document.getElementsByClassName('school-data-search-bar')[0];
		const search_value = search_bar ? search_bar.value : '';
		if(search_value.length > 1)
		{
			const filtered_results = filterArrayOfObjects(filteredResultsRef.current, search_value, ['name', 'course_code']);
			setDisplayResults([...filtered_results]);
		}
		else
		{
			setDisplayResults([...filteredResultsRef.current]);
		}
		
		setIsLoading(false);
		setIsSearching(false);
		setHandleChangeComplete(true);
	}, [dataIsLoading]);
	
	useEffect(() => {
		handleChange();
	}, [debouncedSearchValue, handleChange]);

	const isEditingField = useCallback((course_id, field) => (
		editingCell
		&& `${editingCell.course_id}` === `${course_id}`
		&& editingCell.field === field
	), [editingCell]);

	const cancelCellEdit = useCallback(() => {
		setEditingCell(null);
		setCellDraft(null);
		setIsSavingCell(false);
	}, []);

	const getCourseByID = useCallback((course_id) => (
		databaseData.find((course_info) => `${course_info.course_id}` === `${course_id}`) || null
	), [databaseData]);

	const startCellEdit = useCallback((course_info, field) => {
		if(!course_info) return;

		setEditingCell({course_id: course_info.course_id, field});
		setCellDraft(buildCourseDraft(course_info));
		setIsSavingCell(false);
	}, []);

	useEffect(() => {
		if(!editingCell) return;

		const input_ref_map = {
			course_code: courseCodeInputRef,
			name: courseNameInputRef,
		};
		const input_ref = input_ref_map[editingCell.field];

		if(input_ref?.current)
		{
			input_ref.current.focus();
			if(typeof input_ref.current.select === 'function') input_ref.current.select();
		}
	}, [editingCell]);

	const updateDraftField = (field, value) => {
		setCellDraft((current_draft) => ({...current_draft, [field]: value}));
	};

	const handleSaveCell = useCallback(async (course_info, draft_override = null) => {
		const draft_to_save = draft_override || cellDraft;
		if(!draft_to_save || !editingCell || isSavingCell) return;

		const trimmed_course_code = draft_to_save.course_code ? draft_to_save.course_code.trim() : '';
		const trimmed_course_name = draft_to_save.name ? draft_to_save.name.trim() : '';
		const next_course_type = draft_to_save.course_type || 'general';
		const draft_to_compare = {
			...course_info,
			course_code: trimmed_course_code,
			name: trimmed_course_name,
			is_special: next_course_type === 'special' ? '1' : '0',
			is_pullout: next_course_type === 'pullout' ? '1' : '0',
			is_lunch: next_course_type === 'lunch' ? '1' : '0',
		};

		if(coursesMatch(course_info, draft_to_compare))
		{
			cancelCellEdit();
			return;
		}

		if(!trimmed_course_name)
		{
			swal({
				title: 'Missing a Course Name',
				icon:'warning',
				buttons: {
					cancel: {
						text: 'Okay',
						value: false,
						visible: true,
						className: 'blue-btn'
					}
				},
				content: (
					<div>
						<p>You need to enter a course name before saving!</p>
					</div>
				)
			});
			return;
		}

		setIsSavingCell(true);
		if(draft_override) setCellDraft(draft_override);

		const original_course_id = course_info.course_id;
		const full_course = await loadSpecificData({
			data_type:'course',
			data_id:original_course_id,
			data_version_id:schoolInfo.current_data_version_id,
			schedule_version_id:schoolInfo.current_schedule_version_id,
			school_id:schoolInfo.school_id,
			database:'elementary'
		});

		if(!full_course || full_course.msg)
		{
			setIsSavingCell(false);
			swal({
				title: 'Unable to Save',
				icon:'warning',
				buttons: {
					cancel: {
						text: 'Okay',
						value: false,
						visible: true,
						className: 'blue-btn'
					}
				},
				content: <div><p>We couldn&apos;t load this course&apos;s latest data. Please try again.</p></div>
			});
			return;
		}

		const course_to_save = {
			...full_course,
			course_id: original_course_id,
			course_code: trimmed_course_code,
			name: trimmed_course_name,
			is_special: next_course_type === 'special' ? '1' : '0',
			is_pullout: next_course_type === 'pullout' ? '1' : '0',
			is_lunch: next_course_type === 'lunch' ? '1' : '0',
		};

		const response_data = await saveDataToDatabase({
			school_id:schoolInfo.school_id,
			schedule_version_id:schoolInfo.current_schedule_version_id,
			data_type:'course',
			course_data:course_to_save,
			database:'elementary'
		});

		if(!response_data?.new_course_id)
		{
			setIsSavingCell(false);
			swal({
				title: 'Unable to Save',
				icon:'warning',
				buttons: {
					cancel: {
						text: 'Okay',
						value: false,
						visible: true,
						className: 'blue-btn'
					}
				},
				content: <div><p>We couldn&apos;t save this course value. Please try again.</p></div>
			});
			return;
		}

		const saved_course_id = response_data.new_course_id;
		let saved_course = {
			...course_info,
			...course_to_save,
			course_id: saved_course_id,
		};

		const refreshed_course = await loadSpecificData({
			data_type:'course',
			data_id:saved_course_id,
			data_version_id:schoolInfo.current_data_version_id,
			schedule_version_id:schoolInfo.current_schedule_version_id,
			school_id:schoolInfo.school_id,
			database:'elementary'
		});

		if(refreshed_course && !refreshed_course.msg) saved_course = refreshed_course;

		const next_database_data = mergeSavedCourseIntoList(databaseData, original_course_id, saved_course);
		const next_filtered_results = mergeSavedCourseIntoList(filteredResults, original_course_id, saved_course);

		setDatabaseData(next_database_data);
		setFilteredResults(next_filtered_results);
		if(`${saved_course_id}` !== `${original_course_id}`)
		{
			setPulloutGroups((current_pullout_groups) => current_pullout_groups.map((pullout_group) => (
				`${pullout_group.course_id}` === `${original_course_id}`
					? {...pullout_group, course_id: saved_course_id}
					: pullout_group
			)));
		}
		cancelCellEdit();
	}, [cellDraft, editingCell, isSavingCell, schoolInfo, databaseData, filteredResults, setDatabaseData, setPulloutGroups, cancelCellEdit]);

	const handleCellBlur = (course_info, field) => {
		if(!isEditingField(course_info.course_id, field) || isSavingCell) return;

		window.setTimeout(() => {
			const active_element = document.activeElement;
			const focus_stayed_inside = activeEditorRef.current && active_element ? activeEditorRef.current.contains(active_element) : false;
			if(focus_stayed_inside) return;
			handleSaveCell(course_info);
		}, 0);
	};

	const handleCellKeyDown = async (event, course_info) => {
		if(event.key === 'Enter')
		{
			event.preventDefault();
			await handleSaveCell(course_info);
		}
		else if(event.key === 'Escape')
		{
			event.preventDefault();
			cancelCellEdit();
		}
	};

	const handleTypeSelect = (course_info, course_type) => {
		const next_draft = {...(cellDraft || buildCourseDraft(course_info)), course_type};
		handleSaveCell(course_info, next_draft);
	};

	useEffect(() => {
		if(!editingCell || editingCell.field !== 'course_type') return;

		const handleOutsideClick = (event) => {
			if(activeEditorRef.current && activeEditorRef.current.contains(event.target)) return;

			const course_info = getCourseByID(editingCell.course_id);
			if(!course_info)
			{
				cancelCellEdit();
				return;
			}

			handleSaveCell(course_info);
		};

		document.addEventListener('mousedown', handleOutsideClick);
		return () => document.removeEventListener('mousedown', handleOutsideClick);
	}, [editingCell, handleSaveCell, getCourseByID, cancelCellEdit]);

	const handleSortClick = (sortedData, sorted_by, sort_asc_desc) => {
		cancelCellEdit();
		if(sorted_by === 'course_type_display')
		{
			const sorted_results = [...displayResults].sort((course_a, course_b) => {
				const type_a = getCourseTypeDisplay(course_a);
				const type_b = getCourseTypeDisplay(course_b);
				const comparison = `${type_a}`.localeCompare(`${type_b}`, undefined, {numeric:true, sensitivity:'base'});
				return sort_asc_desc === 'asc' ? comparison : comparison * -1;
			});
			setDisplayResults(sorted_results);
			return;
		}

		setDisplayResults([...sortedData]);
	};
	
	const toggleEditData = (dataID) => {
		cancelCellEdit();
		if(showEditDataModal)
		{
			setShowEditDataModal(false);
			return;
		}

		setCurrentDataID(dataID);
		setShowEditDataModal(true);
	};
	
	const toggleDontShow = () => {
		setDontShowDeletePopup(!dontShowDeletePopup);
	};
	
	const handeDeleteData = async (data_id) => {
		const data = {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, data_type:'course', data_ids:[data_id], database:'elementary'};
		let delete_data_bool = true;
		
		const data_index = databaseData.findIndex((course) => `${course.course_id}` === `${data_id}`);
		const filtered_results_index = filteredResults.findIndex((course) => `${course.course_id}` === `${data_id}`);
		const data_info = databaseData[data_index];
		
		if(!dontShowDeletePopup)
		{
			const options =  {
				title: 'Are you sure?',
				icon: 'warning',
				dangerMode: true,
				buttons: {
					cancel: {
						text: 'Cancel',
						value: false,
						visible: true,
						className: 'gray-btn'
					},
					confirm: {
						text: 'Yes',
						value: true,
						visible: true,
						className: 'red-btn'
					},
				},
				content: (
					<div>
						<div>
							<p>Do you really want to delete {data_info.name} ({data_info.course_code})?</p>
							<p className='red-text'>This will <strong>permanently</strong> remove it from the database.</p>
						</div>
						<div className='sweet-alert-dont-show-message' onClick={() => toggleDontShow('remove')}>
							<label className="checkmark_container">Please don&rsquo;t show me this message again
								<input type="checkbox" />
								<span className="checkmark"></span>
							</label>
						</div>
					</div>
				)
			};
		
			delete_data_bool = await swal(options);
		}
		
		if(delete_data_bool)
		{
			cancelCellEdit();
			databaseData.splice(data_index, 1);
			filteredResults.splice(filtered_results_index, 1);

			setDatabaseData([...databaseData]);
			setFilteredResults([...filteredResults]);
			
			deleteData(data);
		}
	};
	
	useEffect(() => {
		if(!showEditDataModal) setCurrentDataID(null);

		const search_bar = document.getElementsByClassName('school-data-search-bar')[0];
		const search_value = search_bar ? search_bar.value : '';
		if(search_value.length > 0) handleChange();
	}, [showEditDataModal, handleChange]);
	
	useEffect(() => {
		handleChange();
	}, [filteredResults, handleChange]);

	useEffect(() => {
		if(!handleChangeComplete) setFilteredResults([...databaseData]);
	}, [databaseData, dataIsLoading, handleChangeComplete]);
	
	const row_style = {gridTemplateColumns:'1fr 3fr 1fr 70px'};
	
	return (
		<>
		{showEditDataModal &&
			<EditCourseData schoolInfo={schoolInfo} dataID={currentDataID} toggleEditData={toggleEditData} scheduleInfo={scheduleInfo} periods={periods} courses={databaseData} students={students} classrooms={classrooms} teachers={teachers} setDatabaseData={setDatabaseData} setStudents={setStudents} filteredResults={filteredResults} setFilteredResults={setFilteredResults} blocks={blocks} pulloutGroups={pulloutGroups} setPulloutGroups={setPulloutGroups}/>
		}
		{showUploadModal &&
			<UploadModal schoolInfo={schoolInfo} scheduleInfo={scheduleInfo} uploadTitle='Courses' uploadAPIName='coursedata' closeModal={() => setShowUploadModal(false)} handlePullNewData={handlePullNewData}/>
		}
		<div className='school-data-content-screen'>
			<h1 className='school-data-main-heading capitalize'>Courses</h1>
			<div className='school-data-top-bar'>
				<div className='school-data-search-bar-container'>
					<input className='school-data-search-bar' placeholder='Search for courses...' value={searchValue} onChange={(e) => setSearchValue(e.target.value)} onKeyDown={(e) => handleIsSearching(e)} />
					<FontAwesomeIcon className='school-data-search-bar-icon' icon={faSearch}/>
				</div>
				<div className='btn btn-icon-left upload-action-btn' onClick={() => {
					cancelCellEdit();
					setShowUploadModal(true);
				}}>
					<FontAwesomeIcon icon={faUpload} />
					<div>Upload</div>
				</div>
			</div>
			<div className='school-data-database-display'>
				<div className='school-data-database-row school-data-database-display-header fixed-heading-on-scroll' style={row_style}>
					<div className='school-data-database-header-col'>Code <SortToggle handleOnClick={handleSortClick} sortKey='course_code' passedData={displayResults}/></div>
					<div className='school-data-database-header-col'>Course Name <SortToggle handleOnClick={handleSortClick} sortKey='name' passedData={displayResults}/></div>
					<div className='school-data-database-header-col'>Type <SortToggle handleOnClick={handleSortClick} sortKey='course_type_display' passedData={displayResults}/></div>
					<div className='school-data-database-add-new-container' onClick={() => toggleEditData(null)}>
						<div className='school-data-database-add-new-btn'>
							<FontAwesomeIcon className='white-text' icon={faPlus}/>
						</div>
					</div>
				</div>
				{(!handleChangeComplete && isLoading) ?
				(
					<div className='school-data-database-message-container'>
						<img src={require('../../../images/balls.gif')} alt='loading...' style={{height:'80px'}}/>
					</div>
				): isSearching ?
				(
					<div className='school-data-database-message-container'>
						<img src={require('../../../images/searching.gif')} alt='searching...' style={{height:'80px'}}/>
					</div>
				):
				(
					<>
					{displayResults.length === 0 ?
						(
							<div className='school-data-database-message-container'>
								<h4>Sorry, no results were found!</h4>
							</div>
						):
						(
							<>
							{displayResults.map((data_info) => {
								const course_code = (!data_info.course_code) ? '--' : data_info.course_code;
								return (
									<div className='school-data-database-row' style={row_style} key={data_info.course_id}>
										<div
											ref={isEditingField(data_info.course_id, 'course_code') ? activeEditorRef : null}
											className={isEditingField(data_info.course_id, 'course_code') ? 'school-data-inline-editor-container' : 'school-data-inline-editable-cell'}
											onClick={!editingCell ? () => startCellEdit(data_info, 'course_code') : undefined}
										>
											{isEditingField(data_info.course_id, 'course_code') ?
												(isSavingCell ?
													<div className='school-data-inline-saving-cell'>
														<img src={require('../../../images/balls.gif')} alt='saving...' className='school-data-inline-saving-gif'/>
													</div>
												:
													<input
														ref={courseCodeInputRef}
														className='school-data-inline-edit-input'
														value={cellDraft?.course_code || ''}
														disabled={isSavingCell}
														onChange={(e) => updateDraftField('course_code', e.target.value)}
														onKeyDown={(e) => handleCellKeyDown(e, data_info)}
														onBlur={() => handleCellBlur(data_info, 'course_code')}
													/>
												)
											:
												<div>{course_code}</div>
											}
										</div>
										<div
											ref={isEditingField(data_info.course_id, 'name') ? activeEditorRef : null}
											className={isEditingField(data_info.course_id, 'name') ? 'school-data-inline-editor-container' : 'school-data-inline-editable-cell'}
											onClick={!editingCell ? () => startCellEdit(data_info, 'name') : undefined}
										>
											{isEditingField(data_info.course_id, 'name') ?
												(isSavingCell ?
													<div className='school-data-inline-saving-cell'>
														<img src={require('../../../images/balls.gif')} alt='saving...' className='school-data-inline-saving-gif'/>
													</div>
												:
													<input
														ref={courseNameInputRef}
														className='school-data-inline-edit-input'
														value={cellDraft?.name || ''}
														disabled={isSavingCell}
														onChange={(e) => updateDraftField('name', e.target.value)}
														onKeyDown={(e) => handleCellKeyDown(e, data_info)}
														onBlur={() => handleCellBlur(data_info, 'name')}
													/>
												)
											:
												<div>{capitalizeFirstLetters(data_info.name)}</div>
											}
										</div>
										<div
											ref={isEditingField(data_info.course_id, 'course_type') ? activeEditorRef : null}
											className={isEditingField(data_info.course_id, 'course_type') ? 'school-data-inline-editor-container' : 'school-data-inline-editable-cell'}
											onClick={!editingCell ? () => startCellEdit(data_info, 'course_type') : undefined}
										>
											{isEditingField(data_info.course_id, 'course_type') ?
												(isSavingCell ?
													<div className='school-data-inline-saving-cell school-data-inline-saving-cell-floating'>
														<img src={require('../../../images/balls.gif')} alt='saving...' className='school-data-inline-saving-gif'/>
													</div>
												:
													<div className='school-data-inline-select-popover'>
														{COURSE_TYPE_OPTIONS.map((course_type_option) => {
															const course_type_selected = `${cellDraft?.course_type || 'general'}` === `${course_type_option.value}`;

															return (
																<button key={course_type_option.value} type='button' className={`school-data-inline-select-option ${course_type_selected ? 'school-data-inline-select-option-selected' : ''}`} onClick={() => handleTypeSelect(data_info, course_type_option.value)}>
																	{course_type_option.display}
																</button>
															);
														})}
													</div>
												)
											:
												<div>{getCourseTypeDisplay(data_info)}</div>
											}
										</div>
										<div className='school-data-database-row-btn-container'>
											<FontAwesomeIcon className='gray-to-dark-blue-link' icon={faEdit} onClick={() => toggleEditData(data_info.course_id)}/>
											<FontAwesomeIcon className='gray-to-red-link' icon={faTrashAlt} onClick={() => handeDeleteData(data_info.course_id)}/>
										</div>
									</div>
								);
							})}
							</>
						)	
					}
					</>
				)}
			</div>
		</div>
		</>
	);
}
